import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import { LayersDataTypes } from "../../types/layers-data-types";
import { generateDictionaries } from "../../utils/conversion-directory-arkgis";
import { getEmassarData } from "../api";
import { EmassarMethod } from "../../types/emassar-method";
import { Layer } from "../../interfaces/layer";
import {
  transformInvestmentOpportunities,
  transformPlots,
  transformSpacesForRent,
} from "../../utils/helpers-for-filter";
import { LayerType } from "../../types/layer-type";
import { AppDispatch } from "../store";

export interface FiltersState {
  [key: string]: any;
  vacant?: boolean;
  landUses?: number[];
  value?: string;
  minArea?: string | null;
  maxArea?: string | null;
  city?: number | null;
  plotNumber?: string;
  officeNumber?: string;
  floors?: string[];
}

interface AdditionalData {
  landUseOptions: Map<number, string>;
  cities: Map<number, string>;
  estateAr: string[];
  dictionaries: { [key: string]: { [key: string]: string } };
  citiesEn: Map<number, string>;
  citiesAr: string[];
  floors: string[];
}

interface filterDataState {
  data: any[];
  filteredData: any[];
  filters: FiltersState;
  loading: boolean;
  error: string | null;
  filteredLayerIds: number[];
  additionalData: AdditionalData;
}

interface FilterState {
  plots: filterDataState;
  spacesForRent: filterDataState;
  investmentOpportunities: filterDataState;
  selectedCard: any | null;
  previousFid: string | null;
  openedFromMap: boolean;
  selectedCardType:
    | "InvestmentOpportunities"
    | "Plots"
    | "SpacesForRent"
    | null;
  currentOpenedCardPage: "filter" | "cities" | null;
}

const initialfilterState: filterDataState = {
  data: [],
  filteredData: [],
  filters: {},
  loading: false,
  error: null,
  filteredLayerIds: [],
  additionalData: {
    landUseOptions: new Map<number, string>(),
    cities: new Map<number, string>(),
    estateAr: [],
    dictionaries: {},
    citiesEn: new Map<number, string>(),
    citiesAr: [],
    floors: [],
  },
};

const initialState: FilterState = {
  plots: { ...initialfilterState, filters: { vacant: true } },
  investmentOpportunities: { ...initialfilterState, filters: { vacant: true } },
  spacesForRent: { ...initialfilterState, filters: { vacant: true } },
  selectedCard: null,
  previousFid: null,
  openedFromMap: false,
  selectedCardType: null,
  currentOpenedCardPage: null,
};

export const fetchFilterData = createAsyncThunk(
  "filters/fetchFilterData",
  async (
    {
      filterType,
    }: { filterType: "InvestmentOpportunities" | "Plots" | "SpacesForRent" },
    { getState, rejectWithValue },
  ) => {
    try {
      const state: any = getState();
      const layers = state.map.layers.filter(
        (layer: any) =>
          layer.dataType === LayersDataTypes[filterType] &&
          layer.type === LayerType.ArcgisFeatureServer,
      );
      const filteredLayerIds = layers.reduce((acc: number[], layer: Layer) => {
        const id = layer.parentId || layer.id;
        if (!acc.includes(id)) acc.push(id);
        return acc;
      }, []);

      let allFeatures: any[] = [];
      let dictionaries: { [key: string]: { [key: string]: string } } = {};
      let citiesMapEn = new Map<number, string>();
      let citiesMapAr = new Map<number, string>();
      let estateSet = new Set<number>();
      let floorSet = new Set<string>();
      for (const layer of layers) {
        const featureLayer = new FeatureLayer({
          url: layer.url,
          definitionExpression: layer.style2D?.query,
        });
        const query = featureLayer.createQuery();
        const results = await featureLayer.queryFeatures(query);
        const fields = featureLayer.fields;
        if (filterType !== "SpacesForRent")
          dictionaries = { ...dictionaries, ...generateDictionaries(fields) };
        const features = results.features.map((feature) => ({
          ...feature.attributes,
          geometry: feature.geometry,
          layer: layer,
          objectId: feature.getObjectId(),
        }));
        allFeatures = allFeatures.concat(features);
        if (filterType === "SpacesForRent") {
          features.forEach((feature) => {
            if (feature.estateid) estateSet.add(feature.estateid);
            if (feature.estate_e) floorSet.add(feature.estate_e);
          });
        }
      }
      if (filterType === "SpacesForRent") {
        await getEmassarData(EmassarMethod.Estate, Array.from(estateSet)).then(
          (result: any) => {
            if (result) {
              result.forEach((res: any) => {
                citiesMapEn.set(res.estateId, res.name_en);
                if (res.name_ar) citiesMapAr.set(res.estateId, res.name_ar);
              });
            }
          },
        );
      }
      let transformedData;
      if (filterType === "InvestmentOpportunities") {
        transformedData = transformInvestmentOpportunities(
          allFeatures,
          dictionaries,
        );
      } else if (filterType === "Plots") {
        transformedData = transformPlots(allFeatures, dictionaries);
      } else {
        transformedData = transformSpacesForRent(
          allFeatures,
          citiesMapEn,
          citiesMapAr,
        );
      }
      return {
        filterType,
        data: transformedData,
        filteredLayerIds,
        additionalData: {
          landUseOptions: new Map(
            Object.entries(dictionaries["Landuse"] || {})
              .filter(([key]) => key !== "0" && key !== "3")
              .map(([key, value]) => [+key, value]),
          ),
          cities: new Map(
            Object.entries(dictionaries["estateEn"] || {}).map(
              ([key, value]) => [+key, value],
            ),
          ),
          estateAr: Object.values(dictionaries["estateAr"] || {}),
          dictionaries,
          filteredLayerIds: filteredLayerIds,
          citiesEn:
            filterType === "SpacesForRent"
              ? citiesMapEn
              : new Map<number, string>(),
          citiesAr:
            filterType === "SpacesForRent"
              ? Array.from(citiesMapAr.values())
              : [],
          floors:
            filterType === "SpacesForRent"
              ? new Map(
                  Array.from(floorSet.values()).map((value) => [value, value]),
                )
              : [],
        },
      };
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to load data");
    }
  },
);

export const fetchAllArcgisObjects = () => async (dispatch: AppDispatch) => {
  const filterTypes: ("InvestmentOpportunities" | "Plots" | "SpacesForRent")[] =
    ["InvestmentOpportunities", "Plots", "SpacesForRent"];
  await Promise.all(
    filterTypes.map((type) => dispatch(fetchFilterData({ filterType: type }))),
  );
};

const filterData = (data: any[], filters: FiltersState) => {
  let filtered = [...data];
  const {
    vacant,
    landUses,
    value,
    minArea,
    maxArea,
    city,
    plotNumber,
    officeNumber,
    floors,
  } = filters;
  if (vacant)
    filtered = filtered.filter(
      (item) => item.status?.toLowerCase() === "vacant",
    );
  if (value) {
    const lowerValue = value.toLowerCase();
    filtered = filtered.filter((item) =>
      Object.values(item).some(
        (value) => value && value.toString().toLowerCase().includes(lowerValue),
      ),
    );
  }
  if (minArea)
    filtered = filtered.filter(
      (item) => parseFloat(item.area) >= parseFloat(minArea),
    );
  if (maxArea)
    filtered = filtered.filter(
      (item) => parseFloat(item.area) <= parseFloat(maxArea),
    );
  if (landUses && landUses.length > 0)
    filtered = filtered.filter((item) => landUses.includes(item.landUseId));
  if (city) filtered = filtered.filter((item) => item.cityId === city);
  if (plotNumber)
    filtered = filtered.filter((item) => item.number === plotNumber);
  if (officeNumber)
    filtered = filtered.filter((item) => item.number === officeNumber);
  if (floors && floors.length > 0)
    filtered = filtered.filter((item) => floors.includes(item.floorName));
  return filtered;
};

type filterKey = "plots" | "spacesForRent" | "investmentOpportunities";

const mapfilterKey: Record<
  "InvestmentOpportunities" | "Plots" | "SpacesForRent",
  filterKey
> = {
  InvestmentOpportunities: "investmentOpportunities",
  Plots: "plots",
  SpacesForRent: "spacesForRent",
};

export const filtersSlice = createSlice({
  name: "filters",
  initialState,
  reducers: {
    setFilter: (
      state,
      action: PayloadAction<{
        filterType?: "InvestmentOpportunities" | "Plots" | "SpacesForRent";
        filters: Partial<FiltersState>;
      }>,
    ) => {
      const { filterType, filters } = action.payload;
      if (filterType) {
        const key: filterKey = mapfilterKey[filterType];
        state[key].filters = { ...state[key].filters, ...filters };
        state[key].filteredData = filterData(
          state[key].data,
          state[key].filters,
        );
      } else {
        Object.keys(mapfilterKey).forEach((type) => {
          const key = mapfilterKey[type as keyof typeof mapfilterKey];
          state[key].filters = { ...state[key].filters, ...filters };
          state[key].filteredData = filterData(
            state[key].data,
            state[key].filters,
          );
        });
      }
    },

    clearFilters: (
      state,
      action: PayloadAction<{
        filterType?: "InvestmentOpportunities" | "Plots" | "SpacesForRent";
      }>,
    ) => {
      const { filterType } = action.payload;
      if (filterType) {
        const key: filterKey = mapfilterKey[filterType];
        state[key].filters = { ...initialState[key].filters };
        state[key].filteredData = state[key].data;
      } else {
        Object.keys(mapfilterKey).forEach((type) => {
          const key = mapfilterKey[type as keyof typeof mapfilterKey];
          state[key].filters = { ...initialState[key].filters };
          state[key].filteredData = state[key].data;
        });
      }
    },

    applyFilters: (
      state,
      action: PayloadAction<{
        filterType?: "InvestmentOpportunities" | "Plots" | "SpacesForRent";
      }>,
    ) => {
      const { filterType } = action.payload;
      if (filterType) {
        const key: filterKey = mapfilterKey[filterType];
        state[key].filteredData = filterData(
          state[key].data,
          state[key].filters,
        );
      } else {
        Object.keys(mapfilterKey).forEach((type) => {
          const key = mapfilterKey[type as keyof typeof mapfilterKey];
          state[key].filteredData = filterData(
            state[key].data,
            state[key].filters,
          );
        });
      }
    },
    setSelectedCard: (
      state,
      action: PayloadAction<{
        filterType: "InvestmentOpportunities" | "Plots" | "SpacesForRent";
        card: string | any | null;
        openedFromMap: boolean;
      }>,
    ) => {
      const { filterType, card, openedFromMap } = action.payload;
      state.openedFromMap = openedFromMap;
      if (card === null) {
        state.selectedCard = null;
        state.selectedCardType = null;
        return;
      }
      const key: filterKey = mapfilterKey[filterType];
      if (typeof card === "string") {
        const found = state[key].data.find(
          (item) => item.fid?.toString() === card,
        );
        if (!found) {
          state.selectedCard = null;
          state.selectedCardType = null;
          return;
        }
        if (!openedFromMap) {
          state.previousFid = found.fid?.toString();
        }
        state.selectedCard = found;
        state.selectedCardType = filterType;
      } else {
        if (card && state.selectedCard && !openedFromMap) {
          state.previousFid = state.selectedCard.fid?.toString();
        }
        state.selectedCard = card;
        state.selectedCardType = filterType;
      }
    },
    resetSelectedCard: (state) => {
      state.selectedCard = null;
      state.openedFromMap = false;
      state.selectedCardType = null;
    },
    resetPreviousFid: (state) => {
      state.previousFid = null;
    },
    closeSelectedCard: (state) => {
      if (state.openedFromMap && state.previousFid) {
        const key = Object.keys(mapfilterKey).find((t) => {
          const filterKey = mapfilterKey[t as keyof typeof mapfilterKey];
          return state[filterKey].data.some(
            (item) => item.fid?.toString() === state.previousFid,
          );
        }) as "InvestmentOpportunities" | "Plots" | "SpacesForRent";
        const filterKey = mapfilterKey[key];
        const previous = state[filterKey].data.find(
          (item) => item.fid?.toString() === state.previousFid,
        );
        if (previous) {
          state.selectedCard = previous;
          state.openedFromMap = false;
          state.previousFid = null;
        } else {
          state.selectedCard = null;
          state.openedFromMap = false;
          state.previousFid = null;
        }
      } else {
        state.selectedCard = null;
        state.openedFromMap = false;
        state.previousFid = null;
      }
    },
    setCurrentOpenedCardPage: (
      state,
      action: PayloadAction<"filter" | "cities" | null>,
    ) => {
      state.currentOpenedCardPage = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchFilterData.pending, (state, action) => {
      const filterType = (action.meta.arg as { filterType: string }).filterType;
      const key: filterKey =
        mapfilterKey[
          filterType as "InvestmentOpportunities" | "Plots" | "SpacesForRent"
        ];
      state[key].loading = true;
      state[key].error = null;
    });
    builder.addCase(
      fetchFilterData.fulfilled,
      (
        state,
        action: PayloadAction<{
          filterType: "InvestmentOpportunities" | "Plots" | "SpacesForRent";
          data: any[];
          filteredLayerIds: number[];
          additionalData: any;
        }>,
      ) => {
        const { filterType, data, additionalData, filteredLayerIds } =
          action.payload;
        const key: filterKey = mapfilterKey[filterType];
        state[key].loading = false;
        state[key].data = data;
        state[key].filteredData = filterData(data, state[key].filters);
        state[key].filteredLayerIds = filteredLayerIds;
        state[key].additionalData = additionalData;
      },
    );
    builder.addCase(fetchFilterData.rejected, (state, action) => {
      const filterType = (action.meta.arg as { filterType: string }).filterType;
      const key: filterKey =
        mapfilterKey[
          filterType as "InvestmentOpportunities" | "Plots" | "SpacesForRent"
        ];
      state[key].loading = false;
      state[key].error = action.payload as string;
    });
  },
});

export const {
  setFilter,
  clearFilters,
  applyFilters,
  resetSelectedCard,
  setSelectedCard,
  closeSelectedCard,
  resetPreviousFid,
  setCurrentOpenedCardPage,
} = filtersSlice.actions;
export default filtersSlice.reducer;
